import { useEffect, useRef, useState } from 'react'

export interface ViewportState {
  height: number
  keyboardHeight: number
  isKeyboardOpen: boolean
}

const KEYBOARD_OPEN_THRESHOLD = 120
const KEYBOARD_CLOSE_THRESHOLD = 40 // Edge needs tolerance

export const useKeyboardAwareViewportOnce = (): ViewportState => {
  const [state, setState] = useState<ViewportState>({
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    keyboardHeight: 0,
    isKeyboardOpen: false,
  })

  const lockedKeyboardHeightRef = useRef<number | null>(null)
  const keyboardOpenRef = useRef(false)
  const initialInnerHeightRef = useRef<number | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (!initialInnerHeightRef.current) {
      initialInnerHeightRef.current = window.innerHeight
    }

    const update = () => {
      const innerHeight = window.innerHeight

      const viewportHeight = window.visualViewport ? window.visualViewport.height : innerHeight

      const rawKeyboardHeight = Math.max(0, innerHeight - viewportHeight)

      const shouldOpen = rawKeyboardHeight > KEYBOARD_OPEN_THRESHOLD && !keyboardOpenRef.current

      const shouldClose = rawKeyboardHeight < KEYBOARD_CLOSE_THRESHOLD && keyboardOpenRef.current

      if (shouldClose) {
        keyboardOpenRef.current = false
        lockedKeyboardHeightRef.current = null

        setState({
          height: viewportHeight,
          keyboardHeight: 0,
          isKeyboardOpen: false,
        })

        return
      }

      if (shouldOpen) {
        keyboardOpenRef.current = true
        lockedKeyboardHeightRef.current = rawKeyboardHeight

        setState({
          height: viewportHeight,
          keyboardHeight: rawKeyboardHeight,
          isKeyboardOpen: true,
        })

        return
      }

      if (keyboardOpenRef.current && lockedKeyboardHeightRef.current !== null) {
        setState((prev) => ({
          ...prev,
          height: viewportHeight,
        }))
        return
      }

      setState({
        height: viewportHeight,
        keyboardHeight: 0,
        isKeyboardOpen: false,
      })
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', update)
      window.visualViewport.addEventListener('scroll', update)
    }

    window.addEventListener('resize', update)

    update()

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', update)
        window.visualViewport.removeEventListener('scroll', update)
      }
      window.removeEventListener('resize', update)
    }
  }, [])

  return state
}
